<template>
  <div class="element-icon-picker">
    <el-popover
      v-model="visible"
      placement="bottom-start"
      trigger="click"
      popper-class="element-icon-picker-popper"
      :visible-arrow="false"
      @show="syncPageWithSelectedIcon"
    >
      <div class="element-icon-picker__panel">
        <div class="element-icon-picker__toolbar">
          <el-radio-group v-model="displayMode" size="mini">
            <el-radio-button label="icon">仅图标</el-radio-button>
            <el-radio-button label="icon-name">图标 + 名称</el-radio-button>
          </el-radio-group>
          <el-button
            v-if="value"
            type="text"
            class="element-icon-picker__clear"
            @click.stop="clearIcon"
          >
            清空
          </el-button>
        </div>

        <div
          class="element-icon-picker__grid"
          :class="{ 'is-name-visible': displayMode === 'icon-name' }"
        >
          <button
            v-for="iconClass in pagedIcons"
            :key="iconClass"
            type="button"
            class="element-icon-picker__item"
            :class="{ 'is-selected': iconClass === value }"
            :aria-label="'选择 ' + iconClass"
            @click.stop="selectIcon(iconClass)"
          >
            <i :class="iconClass" aria-hidden="true"></i>
            <span
              v-if="displayMode === 'icon-name'"
              class="element-icon-picker__name"
            >
              {{ iconClass }}
            </span>
          </button>
        </div>

        <el-pagination
          class="element-icon-picker__pagination"
          layout="prev, pager, next"
          :current-page="currentPage"
          :page-size="pageSize"
          :total="icons.length"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>

      <button
        slot="reference"
        type="button"
        class="element-icon-picker__trigger"
        :disabled="disabled"
        aria-label="选择菜单图标"
      >
        <i :class="triggerIconClass" aria-hidden="true"></i>
        <i class="el-icon-arrow-down element-icon-picker__arrow" aria-hidden="true"></i>
      </button>
    </el-popover>
  </div>
</template>

<script>
import {
  ELEMENT_ICON_CLASSES,
  isElementIconClass,
} from "@/utils/elementIcons";

const PAGE_SIZE = 48;

export default {
  name: "ElementIconPicker",
  props: {
    value: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: 1,
      displayMode: "icon",
      icons: ELEMENT_ICON_CLASSES,
      pageSize: PAGE_SIZE,
      visible: false,
    };
  },
  computed: {
    pagedIcons() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.icons.slice(start, start + this.pageSize);
    },
    triggerIconClass() {
      if (isElementIconClass(this.value)) {
        return this.value;
      }

      // 历史值不在白名单时不显示原始 class 名，提示用户重新选择。
      return this.value ? "el-icon-warning-outline" : "el-icon-plus";
    },
  },
  methods: {
    handlePageChange(page) {
      this.currentPage = page;
    },
    clearIcon() {
      this.$emit("input", "");
      this.visible = false;
    },
    selectIcon(iconClass) {
      // 图标仅能从静态白名单中选择，避免写入任意 CSS class。
      if (!isElementIconClass(iconClass)) {
        return;
      }

      this.$emit("input", iconClass);
      this.visible = false;
    },
    syncPageWithSelectedIcon() {
      const iconIndex = this.icons.indexOf(this.value);
      if (iconIndex >= 0) {
        this.currentPage = Math.floor(iconIndex / this.pageSize) + 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.element-icon-picker {
  display: inline-block;
  width: 100%;

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 32px;
    padding: 0 10px;
    color: var(--input-text);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: 4px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover,
    &:focus {
      border-color: var(--brand);
      box-shadow: 0 0 0 2px var(--input-focus-ring);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }
  }

  &__arrow {
    color: var(--text-muted);
    font-size: 12px;
  }

  &__panel {
    width: 620px;
    max-width: calc(100vw - 32px);
    padding: 12px;
    background: var(--dialog-bg);
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__clear {
    padding: 0;
    color: var(--text-muted);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    padding: 6px 4px;
    color: var(--text-sub);
    font-size: 21px;
    cursor: pointer;
    background: transparent;
    border: 1px solid var(--input-border);
    border-radius: 4px;
    outline: none;
    transition: background-color 0.2s ease, border-color 0.2s ease,
      color 0.2s ease;

    &:hover,
    &:focus {
      color: var(--brand);
      border-color: var(--brand);
      background: var(--sidebar-active-bg);
    }

    &.is-selected {
      color: var(--brand);
      border-color: var(--brand);
      background: var(--sidebar-active-bg);
    }
  }

  &__grid.is-name-visible &__item {
    flex-direction: column;
    min-height: 76px;
  }

  &__name {
    width: 100%;
    margin-top: 7px;
    overflow: hidden;
    color: var(--text-muted);
    font-size: 10px;
    line-height: 13px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__pagination {
    margin-top: 14px;
    text-align: center;
  }
}
</style>
